import React, { forwardRef, useEffect, useState } from "react";
import { Modal, Select } from "antd/lib";
import { GoogleMap, Libraries, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { Option } from "@/types/common";
import { debounce } from "@/utils/helpers";
import GeocoderResult = google.maps.GeocoderResult;

const libraries: Libraries = ["places"];
const mapContainerStyle = { width: "800px", height: "600px" };
const defaultCenter = { lat: 0, lng: 0 };

export type LocationValue = { placeId: string; lat: number; lng: number; country?: string };

interface LocationSearchProps {
  value?: string;
  onChange: (formattedAddress?: string, location?: LocationValue) => void;
  initialItems?: Option[];
  disabled?: boolean;
}

type MarkerType = { lat: number; lng: number } | null;

const LocationSearch: React.FC<LocationSearchProps> = forwardRef(function LocationSearchField(
  { value: inputValue = "", initialItems = [], onChange, disabled, ...selectProps },
  ref,
) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<Option[]>([]);
  const [value, setValue] = useState<string>();
  const [visible, setVisible] = useState(false);
  const [marker, setMarker] = useState<MarkerType>(null);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY as string,
    libraries,
    language: "en",
  });

  useEffect(() => {
    if (!inputValue) {
      if (value) setValue(undefined);
      return;
    }

    let parsedInputValue: Partial<LocationValue> | undefined;

    try {
      parsedInputValue = JSON.parse(inputValue);
    } catch (err) {
      parsedInputValue = undefined;
    }

    if (!parsedInputValue) {
      if (initialItems.length) {
        const formattedAddress = initialItems.find((item) => item.value.includes(inputValue))?.label;

        if (formattedAddress && formattedAddress !== value) setValue(formattedAddress);
      }

      return;
    }

    const placeId = parsedInputValue.placeId;
    const lat = parsedInputValue.lat;
    const lng = parsedInputValue.lng;

    if (initialItems.length) {
      const formattedAddress = initialItems.find(
        (item) =>
          (placeId && item.value.includes(placeId)) ||
          (lat && item.value.includes(lat.toString())) ||
          (lng && item.value.includes(lng.toString())),
      )?.label;

      if (formattedAddress && formattedAddress !== value) {
        setValue(formattedAddress);
      }

      return;
    }

    if (!isLoaded) return;

    const geocoder = new window.google.maps.Geocoder();

    if (lat && lng) {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          if (status === google.maps.GeocoderStatus.OK && results[0]) {
            setValue(results[0].formatted_address);
          }
        }
      });
    } else if (placeId) {
      geocoder.geocode({ placeId }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          if (status === google.maps.GeocoderStatus.OK && results[0]) {
            setValue(results[0].formatted_address);
          }
        }
      });
    }
  }, [inputValue, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    const autocompleteService = new window.google.maps.places.AutocompleteService();

    if (!value?.length) return;

    const setSuggestionsOptions = () => {
      autocompleteService.getPlacePredictions({ input: value }, async (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestionsOptions = await Promise.all(
            predictions.map(async (prediction) => {
              const value: Partial<LocationValue> = { placeId: prediction.place_id };
              const geocoder = new window.google.maps.Geocoder();

              await geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
                  value.lat = results[0].geometry.location.lat();
                  value.lng = results[0].geometry.location.lng();
                  value.country = getCountry(results[0]);
                }
              });

              return {
                value: JSON.stringify(value),
                label: prediction.description,
              };
            }),
          );

          setSuggestions(suggestionsOptions);
        }
      });
    };

    setSuggestionsOptions();
  }, [value, isLoaded]);

  const handleOnChange = (value?: string, option?: { label: string; value: string }) => {
    if (value && option) {
      const parsedValue: LocationValue = JSON.parse(value);
      setValue(option.label);
      onChange(option.label, {
        placeId: parsedValue.placeId,
        lat: parsedValue.lat,
        lng: parsedValue.lng,
        country: parsedValue.country,
      });
    } else {
      setValue(undefined);
      onChange(undefined, undefined);
    }
  };

  const handleOnSearch = debounce((value: string) => setValue(value || undefined), 100);

  const showModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setVisible(true);
  };
  const handleOk = () => setVisible(false);
  const handleCancel = () => setVisible(false);

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });

    if (isLoaded) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          if (status === google.maps.GeocoderStatus.OK && results[0]) {
            onChange(results[0].formatted_address, {
              placeId: results[0].place_id,
              lat,
              lng,
              country: getCountry(results[0]),
            });
            setValue(results[0].formatted_address);
            handleOk();
          }
        }
      });
    }
  };

  const getCountry = (geocodeResult: GeocoderResult) => {
    return geocodeResult.address_components.find((component) => component.types.includes("country"))?.long_name;
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  return (
    <>
      <Select
        {...selectProps}
        showSearch
        options={suggestions}
        className="w-full"
        onSelect={handleOnChange}
        onClear={handleOnChange}
        onSearch={handleOnSearch}
        placeholder={t("eventCrudPage.form.placeholders.location")}
        value={value}
        defaultActiveFirstOption={!!inputValue}
        filterOption={(input, option) => !!option && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        allowClear
        disabled={disabled}
      />
      <div>
        <a href="#" onClick={showModal}>
          {t("eventCrudPage.form.labels.locationManual")}
        </a>
      </div>
      <Modal
        title={t("eventCrudPage.form.placeholders.location")}
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        width={848}
        style={{ width: 800 }}
      >
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle} // Use the adjusted size for the map container
            center={defaultCenter}
            zoom={1}
            onClick={handleMapClick}
          >
            {marker && <Marker position={marker} />}
          </GoogleMap>
        )}
      </Modal>
    </>
  );
});

export default LocationSearch;
